// ============================================================================
// USE STOCK RESERVATIONS HOOK
// React hook for stock reservation operations
// Date: June 2026
// ============================================================================

import { useState, useCallback } from 'react';
import { authService } from '@/lib/authService';
import type {
  StockAvailabilityCheck,
  ReservationCreateInput,
  ReservationFulfillInput,
  StockReservation,
  ReservationSourceType,
} from '@/lib/inventory/stockReservationService';

export function useStockReservations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Check stock availability for a single item
   */
  const checkAvailability = useCallback(async (input: {
    client_id: number;
    work_item_id: string;
    requested_quantity: number;
    warehouse_id?: number | null;
  }): Promise<StockAvailabilityCheck[]> => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/stock-reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'checkAvailability',
          ...input,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to check stock availability');
      }

      return result.data as StockAvailabilityCheck[];
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to check stock availability';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check stock availability for multiple items
   */
  const checkBulkAvailability = useCallback(async (input: {
    client_id: number;
    items: Array<{ work_item_id: string; requested_quantity: number; warehouse_id?: number | null }>;
  }): Promise<Map<string, StockAvailabilityCheck[]>> => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/stock-reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'checkBulkAvailability',
          ...input,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to check bulk stock availability');
      }

      // Convert object back to Map
      return new Map(Object.entries(result.data as Record<string, StockAvailabilityCheck[]>));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to check bulk stock availability';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a stock reservation
   */
  const createStockReservation = useCallback(async (input: Omit<ReservationCreateInput, 'reserved_by'>): Promise<StockReservation> => {
    setLoading(true);
    setError(null);
    try {
      const user = (authService as unknown as { user?: { id: number } | null }).user;
      const reservedBy = user?.id || 0;

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/stock-reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'createReservation',
          ...input,
          reserved_by: reservedBy,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to create reservation');
      }

      return result.data as StockReservation;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create reservation';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create multiple stock reservations
   */
  const createBulkStockReservations = useCallback(async (input: {
    client_id: number;
    reservations: Array<Omit<ReservationCreateInput, 'reserved_by'>>;
  }): Promise<StockReservation[]> => {
    setLoading(true);
    setError(null);
    try {
      const user = (authService as unknown as { user?: { id: number } | null }).user;
      const reservedBy = user?.id || 0;

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/stock-reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'createBulkReservations',
          client_id: input.client_id,
          reservations: input.reservations.map(r => ({ ...r, reserved_by: reservedBy })),
          reserved_by: reservedBy,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to create bulk reservations');
      }

      return result.data as StockReservation[];
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create bulk reservations';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fulfill a reservation
   */
  const fulfillStockReservation = useCallback(async (input: Omit<ReservationFulfillInput, 'fulfilled_by'>): Promise<StockReservation> => {
    setLoading(true);
    setError(null);
    try {
      const user = (authService as unknown as { user?: { id: number } | null }).user;
      const fulfilledBy = user?.id || 0;

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/stock-reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'fulfillReservation',
          ...input,
          fulfilled_by: fulfilledBy,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fulfill reservation');
      }

      return result.data as StockReservation;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fulfill reservation';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cancel a reservation
   */
  const cancelStockReservation = useCallback(async (input: {
    reservation_id: string;
    reason?: string | null;
  }): Promise<StockReservation> => {
    setLoading(true);
    setError(null);
    try {
      const user = (authService as unknown as { user?: { id: number } | null }).user;
      const cancelledBy = user?.id || 0;

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/stock-reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'cancelReservation',
          reservation_id: input.reservation_id,
          cancelled_by: cancelledBy,
          reason: input.reason,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to cancel reservation');
      }

      return result.data as StockReservation;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to cancel reservation';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cancel all reservations for a source document
   */
  const cancelSourceReservations = useCallback(async (input: {
    client_id: number;
    source_type: ReservationSourceType;
    source_id: string;
    reason?: string | null;
  }): Promise<StockReservation[]> => {
    setLoading(true);
    setError(null);
    try {
      const user = (authService as unknown as { user?: { id: number } | null }).user;
      const cancelledBy = user?.id || 0;

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/stock-reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'cancelSourceReservations',
          client_id: input.client_id,
          source_type: input.source_type,
          source_id: input.source_id,
          cancelled_by: cancelledBy,
          reason: input.reason,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to cancel source reservations');
      }

      return result.data as StockReservation[];
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to cancel source reservations';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get reservations for a source document
   */
  const getSourceReservations = useCallback(async (input: {
    client_id: number;
    source_type: ReservationSourceType;
    source_id: string;
  }): Promise<StockReservation[]> => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/stock-reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'getSourceReservations',
          ...input,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to get source reservations');
      }

      return result.data as StockReservation[];
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to get source reservations';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get pending reservations for an item
   */
  const getItemPendingReservations = useCallback(async (input: {
    client_id: number;
    work_item_id: string;
    warehouse_id?: number | null;
  }): Promise<StockReservation[]> => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/inventory/stock-reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'getPendingReservationsForItem',
          ...input,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to get pending reservations');
      }

      return result.data as StockReservation[];
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to get pending reservations';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    clearError,
    checkAvailability,
    checkBulkAvailability,
    createStockReservation,
    createBulkStockReservations,
    fulfillStockReservation,
    cancelStockReservation,
    cancelSourceReservations,
    getSourceReservations,
    getItemPendingReservations,
  };
}